import i18next from 'i18next';
import common_en from './translations/en/common.json';
import mvp_en from './translations/en/mvp.json';
import dentaquest_en from './translations/en/dentaquest.json';
import ahc_en from './translations/en/ahc.json';
import ahcadmin_en from './translations/en/ahcadmin.json';
import transport_en from './translations/en/transport.json';
import { mergeTranslationResources } from './i18nUtils';
import type { TranslationResource } from './i18nUtils';
import { getPortalName } from './hooks/usePortalName';
import { MEMBER_PORTAL_NAMES } from './data/constants';

const COMMON_NS = 'common';

/** Bundled (compiled-in) translations from `src/translations/en`, by namespace. */
const bundledTranslations: Record<string, TranslationResource> = {
  common: common_en,
  mvp: mvp_en,
  dentaquest: dentaquest_en,
  ahc: ahc_en,
  ahcadmin: ahcadmin_en,
  transport: transport_en,
};

const NAMESPACES = Object.keys(bundledTranslations);

const isRecord = (value: unknown): value is TranslationResource =>
  Object.prototype.toString.call(value) === '[object Object]';

/**
 * Fetch a single `public/<ns>.json` override file. These are deployed alongside
 * the build so translations can be patched per-environment without a rebuild.
 * A missing / non-OK / non-object response is treated as "no overrides" ({}).
 */
const loadPublicTranslation = async (
  ns: string,
): Promise<TranslationResource> => {
  try {
    const response = await fetch(`/en_${ns}.json`, { cache: 'no-cache' });
    if (!response.ok) {
      return {};
    }
    const translations = await response.json();
    return isRecord(translations) ? translations : {};
  } catch {
    return {};
  }
};

/** Load every public override file (common + each portal namespace) in parallel. */
const loadPublicTranslations = async (): Promise<
  Record<string, TranslationResource>
> => {
  const entries = await Promise.all(
    NAMESPACES.map(
      async (ns) => [ns, await loadPublicTranslation(ns)] as const,
    ),
  );
  return Object.fromEntries(entries);
};

/**
 * Build a namespace bundle that honors the translation resolution priority
 * (highest wins):
 *   1. local portal file   — public/<portal>.json
 *   2. local common file   — public/common.json
 *   3. bundled portal file — src/translations/en/<portal>.json
 *   4. bundled common file — src/translations/en/common.json
 *
 * `mergeTranslationResources(base, override)` lets `override` win on conflicts,
 * so we layer from lowest priority to highest. When `portalNs` is null (no
 * subdomain portal) the portal layers collapse to {} and this reduces to
 * "local common over bundled common".
 */
const buildNamespaceResource = (
  bundled: Record<string, TranslationResource>,
  local: Record<string, TranslationResource>,
  portalNs: string | null,
): TranslationResource => {
  const bundledCommon = bundled[COMMON_NS] ?? {};
  const localCommon = local[COMMON_NS] ?? {};
  const bundledPortal = (portalNs && bundled[portalNs]) || {};
  const localPortal = (portalNs && local[portalNs]) || {};

  return mergeTranslationResources(
    mergeTranslationResources(
      mergeTranslationResources(bundledCommon, bundledPortal),
      localCommon,
    ),
    localPortal,
  );
};

/** Resolve the active subdomain portal, defaulting to null if env isn't ready. */
const resolveActivePortal = (): string | null => {
  try {
    return getPortalName();
  } catch {
    return null;
  }
};

export const initializeI18n = async (): Promise<void> => {
  const local = await loadPublicTranslations();
  const activePortal = resolveActivePortal();

  // The `common` namespace is what most consumers resolve against (directly, or
  // via `fallbackNS`), so bake the active portal's full priority chain into it.
  const resources: Record<string, TranslationResource> = {
    [COMMON_NS]: buildNamespaceResource(
      bundledTranslations,
      local,
      activePortal,
    ),
  };
  // Each portal namespace gets the same full chain for that portal so code that
  // reads a specific portal namespace (e.g. useCommonTranslation) resolves
  // identically to the common path.
  for (const ns of MEMBER_PORTAL_NAMES) {
    resources[ns] = buildNamespaceResource(bundledTranslations, local, ns);
  }

  await i18next.init({
    interpolation: { escapeValue: false },
    lng: 'en',
    ns: NAMESPACES,
    fallbackNS: COMMON_NS,
    resources: {
      en: resources,
    },
  });
};
